/* eslint-disable sonarjs/cognitive-complexity */
import React, {type FC, useCallback, useState} from 'react';
import {
  IProduct,
  IProductGroup,
  IProductGroupAssignment,
  IProductOptionSelection,
} from '../../../../types/galleryTypes';
import {IProductSelectionAvailabilityMap} from '@wix/wixstores-client-core/dist/es/src/productVariantCalculator/ProductVariantCalculator';
import s from './ProductOptions.scss';
import {ProductOptionType} from '@wix/wixstores-graphql-schema/dist/es/src/graphql-schema';
import classNames from 'classnames';
import {useGlobals, withGlobals} from '../../../../globalPropsContext';
import {IGalleryGlobalProps} from '../../../../gallery/galleryGlobalStrategy';
import {Experiments} from '../../../../constants';
import {ProductColorOption} from './ProductColorOption/ProductColorOption';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {getAllVisibleColorOptions, getAllVisibleTextualOptions} from './utils/getVisibleOptions';
import {ProductTextualOption} from './ProductTextualOptions/ProductTextualOption';
import {ConditionalRender} from '../../ConditionalRender/ConditionalRender';
import {useOptionFocusManagement} from './utils/hooks/useOptionFocusManagement';

function buildGroupedColorOption(option: IProduct['options'][0], productGroup: IProductGroup): IProduct['options'][0] {
  const selections = productGroup.assignments
    .filter((a: IProductGroupAssignment) => a.status === 'ACTIVE')
    .map((a: IProductGroupAssignment) => ({
      id: Number(a.choice.id),
      value: a.choice.value,
      description: a.choice.name,
      key: a.productId,
      inStock: a.stockStatus === 'IN_STOCK',
      visible: true,
      linkedMediaItems: [],
    })) as unknown as IProductOptionSelection[];
  return {...option, selections};
}

function buildGroupedTextualOption(
  option: IProduct['options'][0],
  productGroup: IProductGroup
): IProduct['options'][0] {
  const selections = productGroup.assignments
    .filter((a: IProductGroupAssignment) => a.status === 'ACTIVE')
    .map((a: IProductGroupAssignment) => ({
      id: Number(a.choice.id),
      value: a.choice.name,
      description: a.choice.name,
      key: a.productId,
      inStock: a.stockStatus === 'IN_STOCK',
      visible: true,
      linkedMediaItems: [],
    })) as unknown as IProductOptionSelection[];
  return {...option, selections};
}

function buildGroupedSelectionsAvailability(productGroup: IProductGroup) {
  return Object.fromEntries(
    productGroup.assignments
      .filter((a: IProductGroupAssignment) => a.status === 'ACTIVE')
      .map((a: IProductGroupAssignment) => [
        Number(a.choice.id),
        {isVisible: true, isSelectable: a.stockStatus === 'IN_STOCK'},
      ])
  );
}

function getCombinedListingSelectionIds(productGroup: IProductGroup, productId: string): number[] {
  const myAssignment = productGroup.assignments.find((a: IProductGroupAssignment) => a.productId === productId);
  return myAssignment ? [Number(myAssignment.choice.id)] : [];
}

type IProductOption = IProduct['options'][0];
export interface ProductOptionsChangeData {
  optionType: ProductOptionType;
}

export interface IProductOptionsProps extends IGalleryGlobalProps {
  product: IProduct;
  cardIndex: number;
  selectionIds: number[];
  isLoading?: boolean;
  readOnly?: boolean;

  onSelectionIdsChange(
    optionSelections: IProductOptionsProps['selectionIds'],
    changeData: ProductOptionsChangeData
  ): Promise<void>;

  variantsAvailability: IProductSelectionAvailabilityMap;
}

export interface OptionRenderParams {
  option: IProduct['options'][number];
  availability: IProductSelectionAvailabilityMap[string];
  url?: string;
}

export enum DataHook {
  ProductOption = 'product-option',
  DropdownOption = 'product-options-dropdown',
  ColorOption = 'product-options-color',
  ColorOptionMore = 'product-options-color-more',
  OptionWrapper = 'product-option-wrapper',
  ButtonOption = 'product-option-button',
  ButtonOptionMore = 'product-options-button-more',
  ProductOptionLoader = 'product-option-loader',
}

const ProductOptionsComponent: FC<IProductOptionsProps> = ({
  product,
  cardIndex,
  selectionIds,
  isLoading,
  onSelectionIdsChange,
  variantsAvailability,
  readOnly,
}) => {
  const {productsManifest, productGroupsMap, handleCombinedListingSiblingSwitch} = useGlobals();
  const productGroup: IProductGroup | undefined = productGroupsMap?.[product.id];
  const {experiments} = useExperiments();
  const isQualityOptionsStylingChangesEnabled = experiments.enabled(Experiments.EnableQualityOptionsStylingChanges);
  const shouldShowFirstProductOptionInGallery = experiments.enabled(Experiments.ShouldShowFirstProductOptionInGallery);
  const [latestActiveOptionId, setLatestActiveOptionId] = useState<string>('');

  const isOptionSelected = useCallback(
    (option: IProductOption) => option.selections.find(({id}) => selectionIds.includes(id)) !== undefined,
    [selectionIds]
  );

  const {registerOptionRef} = useOptionFocusManagement({product, isOptionSelected, cardIndex});

  const shouldRenderLoader = useCallback(
    (optionId: string) => {
      return isLoading && latestActiveOptionId !== optionId;
    },
    [isLoading, latestActiveOptionId]
  );

  const handleOptionChange = useCallback(
    async (selectedOptionIds: number[], changeData: ProductOptionsChangeData, optionId: string) => {
      setLatestActiveOptionId(optionId);
      return onSelectionIdsChange(selectedOptionIds, changeData);
    },
    [onSelectionIdsChange]
  );

  const createGroupedSelectionChangeHandler = useCallback(
    (renderedOption: IProductOption) => {
      return async (selectedOptionIds: number[], _changeData: ProductOptionsChangeData) => {
        const clickedId = selectedOptionIds[selectedOptionIds.length - 1];
        const siblingProductId = renderedOption.selections.find((sel) => sel.id === clickedId)?.key;
        if (siblingProductId && handleCombinedListingSiblingSwitch) {
          await handleCombinedListingSiblingSwitch(product.id, siblingProductId, cardIndex);
        }
      };
    },
    [handleCombinedListingSiblingSwitch, product, cardIndex]
  );

  const renderGroupedByColorOption = useCallback(
    (option: IProductOption, group: IProductGroup) => {
      const renderedOption = buildGroupedColorOption(option, group);

      return (
        <div
          key={option.id}
          ref={(ref) => registerOptionRef(option.id, ref)}
          className={classNames({
            [s.color]: isQualityOptionsStylingChangesEnabled,
            [s.oldColor]: !isQualityOptionsStylingChangesEnabled,
          })}
          data-hook={DataHook.OptionWrapper}>
          <ProductColorOption
            url={productsManifest[product.id].url}
            option={renderedOption}
            selectionsAvailability={buildGroupedSelectionsAvailability(group)}
            selectionIds={getCombinedListingSelectionIds(group, product.id)}
            onSelectionIdsChange={createGroupedSelectionChangeHandler(renderedOption)}
            isLoading={shouldRenderLoader(option.id)}
            isGroupedByOption
          />
        </div>
      );
    },
    [
      createGroupedSelectionChangeHandler,
      isQualityOptionsStylingChangesEnabled,
      productsManifest,
      shouldRenderLoader,
      product,
      registerOptionRef,
    ]
  );

  const renderColorOption = useCallback(
    (option: IProductOption) => {
      return (
        <div
          key={option.id}
          ref={(ref) => registerOptionRef(option.id, ref)}
          className={classNames({
            [s.color]: isQualityOptionsStylingChangesEnabled,
            [s.oldColor]: !isQualityOptionsStylingChangesEnabled,
          })}
          data-hook={DataHook.OptionWrapper}>
          <ProductColorOption
            url={productsManifest[product.id].url}
            option={option}
            selectionsAvailability={variantsAvailability[option.id]}
            selectionIds={selectionIds}
            onSelectionIdsChange={(selectedOptionIds: number[], changeData: ProductOptionsChangeData) =>
              handleOptionChange(selectedOptionIds, changeData, option.id)
            }
            isLoading={shouldRenderLoader(option.id)}
            isGroupedByOption={false}
          />
        </div>
      );
    },
    [
      handleOptionChange,
      isQualityOptionsStylingChangesEnabled,
      productsManifest,
      selectionIds,
      shouldRenderLoader,
      variantsAvailability,
      product,
      registerOptionRef,
    ]
  );

  const getColorOptions = useCallback(() => {
    const colorOptions = getAllVisibleColorOptions(product.options);
    return colorOptions.map((option) =>
      productGroup && productGroup.customizationId === option.id
        ? renderGroupedByColorOption(option, productGroup)
        : renderColorOption(option)
    );
  }, [renderColorOption, renderGroupedByColorOption, product.options, productGroup]);

  const renderGroupedByTextualOption = useCallback(
    (option: IProductOption, group: IProductGroup) => {
      const renderedOption = buildGroupedTextualOption(option, group);

      return (
        <div
          key={option.id}
          ref={(ref) => registerOptionRef(option.id, ref)}
          className={classNames({
            [s.dropdown]: isQualityOptionsStylingChangesEnabled,
            [s.oldDropdown]: !isQualityOptionsStylingChangesEnabled,
          })}
          data-hook={DataHook.OptionWrapper}>
          <ProductTextualOption
            productUrl={productsManifest[product.id].url}
            option={renderedOption}
            availability={buildGroupedSelectionsAvailability(group)}
            selectionIds={getCombinedListingSelectionIds(group, product.id)}
            readOnly={readOnly}
            isLoading={shouldRenderLoader(option.id)}
            onSelectionIdsChange={createGroupedSelectionChangeHandler(renderedOption)}
            isGroupedByOption
          />
        </div>
      );
    },
    [
      createGroupedSelectionChangeHandler,
      isQualityOptionsStylingChangesEnabled,
      productsManifest,
      shouldRenderLoader,
      product,
      readOnly,
      registerOptionRef,
    ]
  );

  const renderTextualOption = useCallback(
    (option: IProductOption) => {
      return (
        <div
          key={option.id}
          ref={(ref) => registerOptionRef(option.id, ref)}
          className={classNames({
            [s.dropdown]: isQualityOptionsStylingChangesEnabled,
            [s.oldDropdown]: !isQualityOptionsStylingChangesEnabled,
          })}
          data-hook={DataHook.OptionWrapper}>
          <ProductTextualOption
            productUrl={productsManifest[product.id].url}
            option={option}
            availability={variantsAvailability[option.id]}
            selectionIds={selectionIds}
            readOnly={readOnly}
            isLoading={shouldRenderLoader(option.id)}
            onSelectionIdsChange={(selectedOptionIds: number[], changeData: ProductOptionsChangeData) =>
              handleOptionChange(selectedOptionIds, changeData, option.id)
            }
            isGroupedByOption={false}
          />
        </div>
      );
    },
    [
      handleOptionChange,
      isQualityOptionsStylingChangesEnabled,
      productsManifest,
      selectionIds,
      shouldRenderLoader,
      variantsAvailability,
      product,
      readOnly,
      registerOptionRef,
    ]
  );

  const getTextualOptions = useCallback(() => {
    const textualOptions = getAllVisibleTextualOptions(product.options);
    return textualOptions.map((option) =>
      productGroup && productGroup.customizationId === option.id
        ? renderGroupedByTextualOption(option, productGroup)
        : renderTextualOption(option)
    );
  }, [renderTextualOption, renderGroupedByTextualOption, product.options, productGroup]);

  const getFirstOption = useCallback(() => {
    const firstOption = product.options[0];
    if (!firstOption) {
      return null;
    }

    if (productGroup && productGroup.customizationId === firstOption.id) {
      return firstOption.optionType === ProductOptionType.COLOR
        ? renderGroupedByColorOption(firstOption, productGroup)
        : renderGroupedByTextualOption(firstOption, productGroup);
    }
    if (firstOption.optionType === ProductOptionType.COLOR) {
      return renderColorOption(firstOption);
    }
    return renderTextualOption(firstOption);
  }, [
    renderColorOption,
    renderGroupedByColorOption,
    renderTextualOption,
    renderGroupedByTextualOption,
    product.options,
    productGroup,
  ]);

  return shouldShowFirstProductOptionInGallery ? (
    <>
      <ConditionalRender by="showFirstOptionAnyType">{getFirstOption()}</ConditionalRender>
      <ConditionalRender by="showColorOption">{getColorOptions()}</ConditionalRender>
      <ConditionalRender by="showTextualOptions">{getTextualOptions()}</ConditionalRender>
    </>
  ) : (
    <>
      {getColorOptions()}
      <ConditionalRender by="showTextualOptionsWhenShowFirstOptionAnyTypeExperimentIsDisabled">
        {getTextualOptions()}
      </ConditionalRender>
    </>
  );
};

export const ProductOptions = withGlobals(ProductOptionsComponent);
